import Swiper from 'swiper';
import { Navigation, Mousewheel } from 'swiper/modules';
import 'swiper/swiper-bundle.css';
import 'swiper/css/navigation';

/**
 * Mega Menu — desktop hover panels + mobile accordion.
 * Data source: includes/megamenu-data.php (single PHP array, spec §9).
 * Behaviour spec: documents/bepro/mega-menu-spec.md §6, §8.
 */

const CLOSE_DELAY = 160; // ms — spec §6
const MOBILE_BREAKPOINT = 1024; // matches SCSS `lg` — abstracts/_variables.scss

function initDesktopMegaMenu() {
	const nav = document.querySelector('[data-mega-menu]');
	const viewport = nav ? nav.querySelector('[data-mega-menu-viewport]') : null;
	const backdrop = document.querySelector('[data-mega-menu-backdrop]');

	if (!nav || !viewport) {
		return;
	}

	const items = Array.from(nav.querySelectorAll('[data-mega-menu-item]'));
	let activeSlug = null;
	let closeTimer = null;

	const getPanel = (slug) => viewport.querySelector(`[data-mega-menu-panel-slug="${slug}"]`);

	const clearCloseTimer = () => {
		if (closeTimer) {
			clearTimeout(closeTimer);
			closeTimer = null;
		}
	};

	const setHeightToPanel = (panel) => {
		const inner = panel.querySelector('.mega-menu__panel-inner');
		const height = inner ? inner.offsetHeight : panel.offsetHeight;
		viewport.style.height = `${height}px`;
	};

	const openPanel = (slug) => {
		const panel = getPanel(slug);
		if (!panel) {
			return;
		}

		clearCloseTimer();

		const wasOpen = activeSlug !== null;
		viewport.dataset.state = wasOpen ? 'switching' : 'opening';

		// Directional switch (round-5): new content enters from the side the
		// user is hovering toward, based on the two toggles' order in the nav.
		if (wasOpen) {
			const prevIndex = items.findIndex((item) => item.dataset.megaMenuSlug === activeSlug);
			const nextIndex = items.findIndex((item) => item.dataset.megaMenuSlug === slug);
			viewport.dataset.direction = nextIndex > prevIndex ? 'right' : 'left';
		}

		items.forEach((item) => {
			const toggle = item.querySelector('[data-mega-menu-toggle]');
			if (toggle) {
				toggle.setAttribute('aria-expanded', String(item.dataset.megaMenuSlug === slug));
			}
		});

		viewport.querySelectorAll('[data-mega-menu-panel]').forEach((p) => {
			p.classList.toggle('is-active', p === panel);
		});

		activeSlug = slug;
		viewport.classList.add('is-open');

		// Backdrop shows once on first open and stays put through switches
		// between panels — it shouldn't flicker every time the user hovers
		// a different top-level item (round-7).
		if (!wasOpen && backdrop) {
			backdrop.classList.add('is-visible');
		}

		// Height-morph: measure the panel once it's laid out as active.
		requestAnimationFrame(() => setHeightToPanel(panel));
	};

	const closePanel = () => {
		if (!activeSlug) {
			return;
		}

		viewport.dataset.state = 'closing';
		viewport.style.height = '0px';
		viewport.classList.remove('is-open');
		activeSlug = null;

		if (backdrop) {
			backdrop.classList.remove('is-visible');
		}

		items.forEach((item) => {
			const toggle = item.querySelector('[data-mega-menu-toggle]');
			if (toggle) {
				toggle.setAttribute('aria-expanded', 'false');
			}
		});

		viewport.querySelectorAll('[data-mega-menu-panel]').forEach((p) => {
			p.classList.remove('is-active');
		});
	};

	const scheduleClose = () => {
		clearCloseTimer();
		closeTimer = setTimeout(closePanel, CLOSE_DELAY);
	};

	items.forEach((item) => {
		const slug = item.dataset.megaMenuSlug;
		const toggle = item.querySelector('[data-mega-menu-toggle]');

		if (!toggle) {
			return; // plain top-level link (Prodajna mjesta, Kontakt) — nothing to wire
		}

		item.addEventListener('mouseenter', () => {
			if (window.innerWidth >= MOBILE_BREAKPOINT) {
				openPanel(slug);
			}
		});

		// Click = toggle, for touch devices and keyboard users (spec §6).
		toggle.addEventListener('click', (event) => {
			event.preventDefault();
			if (activeSlug === slug) {
				closePanel();
			} else {
				openPanel(slug);
			}
		});
	});

	nav.addEventListener('mouseleave', scheduleClose);
	nav.addEventListener('mouseenter', clearCloseTimer);

	// Entering the open panel itself cancels the pending close (spec §6:
	// "ulazak u panel poništava zatvaranje").
	viewport.addEventListener('mouseenter', clearCloseTimer);
	viewport.addEventListener('mouseleave', scheduleClose);

	// Close on a normal link click inside a panel.
	viewport.addEventListener('click', (event) => {
		if (event.target.closest('a')) {
			closePanel();
		}
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && activeSlug) {
			const toggle = nav.querySelector(`[data-mega-menu-slug="${activeSlug}"] [data-mega-menu-toggle]`);
			closePanel();
			if (toggle) {
				toggle.focus();
			}
		}
	});

	document.addEventListener('click', (event) => {
		if (activeSlug && !nav.contains(event.target)) {
			closePanel();
		}
	});

	window.addEventListener('resize', () => {
		if (activeSlug) {
			const panel = getPanel(activeSlug);
			if (panel) {
				setHeightToPanel(panel);
			}
		}
	});
}

function initMobileMegaMenu() {
	const nav = document.querySelector('[data-mega-menu-mobile]');
	if (!nav) {
		return;
	}

	const toggles = Array.from(nav.querySelectorAll('[data-mega-menu-mobile-toggle]'));
	const backButtons = Array.from(nav.querySelectorAll('[data-mega-menu-mobile-back]'));
	let activeToggle = null;

	const getPanel = (slug) => nav.querySelector(`[data-mega-menu-mobile-panel][data-slug="${slug}"]`);

	const drillInto = (slug, toggle) => {
		const panel = getPanel(slug);
		if (!panel) {
			return;
		}

		nav.querySelectorAll('[data-mega-menu-mobile-panel]').forEach((p) => {
			p.classList.toggle('is-active', p === panel);
		});

		toggles.forEach((t) => t.setAttribute('aria-expanded', String(t === toggle)));

		nav.classList.add('is-drilled');
		activeToggle = toggle || null;
		// Safety net: `nav` must stay at scrollLeft 0 — the slide is driven
		// entirely by the track's `transform`, not native scrolling.
		nav.scrollLeft = 0;

		// Each panel scrolls independently (`__panel-body`) — always land at
		// the top of the section being entered.
		const body = panel.querySelector('.mega-menu-mobile__panel-body');
		if (body) {
			body.scrollTop = 0;
		}

		// Spec: focus the first element of level 2 (the back button) on entry.
		// `preventScroll` is required here — `nav` is `overflow: hidden` and
		// drives its own slide via `transform`; without it the browser's
		// default focus-scroll-into-view fights that transform and knocks
		// the track's scroll position off (visible as cropped/offset panels).
		const backBtn = panel.querySelector('[data-mega-menu-mobile-back]');
		if (backBtn) {
			// Wait for the slide-in transition to start so focus doesn't jump
			// before the panel is visible to a screen magnifier / keyboard user.
			window.setTimeout(() => backBtn.focus({ preventScroll: true }), 50);
		}
	};

	const drillBack = () => {
		if (!nav.classList.contains('is-drilled')) {
			return;
		}

		nav.classList.remove('is-drilled');
		toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
		nav.scrollLeft = 0;

		if (activeToggle) {
			activeToggle.focus({ preventScroll: true });
		}
		activeToggle = null;
	};

	toggles.forEach((toggle) => {
		toggle.addEventListener('click', () => drillInto(toggle.dataset.target, toggle));
	});

	backButtons.forEach((btn) => {
		btn.addEventListener('click', drillBack);
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && nav.classList.contains('is-drilled')) {
			drillBack();
		}
	});

	// Reset to level 1 whenever the drawer itself is opened/closed, so the
	// next open always starts fresh (spec §8 + round-2 feedback).
	document.querySelectorAll('.js-menu-toggle').forEach((hamburger) => {
		hamburger.addEventListener('click', () => {
			nav.classList.remove('is-drilled');
			toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
			activeToggle = null;
		});
	});
}

function initShopSlider() {
	const el = document.querySelector('[data-mega-menu-shop-slider]');
	if (!el) {
		return;
	}

	// Round-5 spec: 3 cards visible + peek of a 4th, drag, no autoplay (it
	// would fight the hover-driven mega menu). Same Swiper already used by
	// resources/js/blocks/productSlider.js — no new library added.
	// eslint-disable-next-line no-new
	new Swiper(el, {
		modules: [Navigation, Mousewheel],
		slidesPerView: 1.15,
		spaceBetween: 14,
		grabCursor: true,
		navigation: {
			prevEl: '[data-mega-menu-shop-prev]',
			nextEl: '[data-mega-menu-shop-next]',
		},
		// Round-8: trackpad/wheel scroll. `forceToAxis` keeps vertical page
		// scroll working normally (only horizontal wheel/trackpad gestures
		// drive the slider); `releaseOnEdges` hands scroll back to the page
		// once the slider hits either end instead of eating the gesture.
		mousewheel: {
			forceToAxis: true,
			releaseOnEdges: true,
		},
		breakpoints: {
			640: { slidesPerView: 2.15 },
			1024: { slidesPerView: 2.4 },
			1280: { slidesPerView: 3.3 },
		},
	});
}

export default function MegaMenu() {
	initDesktopMegaMenu();
	initMobileMegaMenu();
	initShopSlider();
}
